import _ from 'lodash';

import { polymorphicIsEqual } from './inputState';

export const countUnique = <T,>(iterable: T[]): number => new Set(iterable).size;
export const sortAndUniqBy = <T, Key extends keyof T>(
    iterable: T[],
    {
        sortBy,
        uniqBy,
    }: {
        sortBy: Key;
        uniqBy: Key;
    },
): T[] => _.uniqBy(_.sortBy(iterable, sortBy), uniqBy);

export const uniqueByEquality = <T,>(iterable: T[]): T[] => _.uniqWith(iterable, _.isEqual);

// Remove keys with undefined values from an object or array.
export const removeUndefinedValues = (obj: Record<string, unknown>) =>
    _.pickBy(obj, (value) => value !== undefined);
export const removeUndefinedFromArray = <T,>(arr: (T | undefined)[]): T[] =>
    arr.filter((element) => element !== undefined) as T[];
export const removeNullFromArray = <T,>(arr: (T | null)[]): T[] =>
    arr.filter((element) => element !== null) as T[];

// Sometimes we want to manipulate an array like a set, adding a value
//  if it is not there, or removing a value if it is there.
// Note: These break if moment objects are involved!
export const arrayWithValue = <T,>(array: T[], value: T): T[] =>
    uniqueByEquality(_.concat(array, value));
export const arrayWithValues = <T,>(array: T[], values: T[]): T[] =>
    uniqueByEquality(_.concat(array, values));
export const arrayWithoutValue = <T,>(array: T[], value: T): T[] => _.without(array, value);
export const arrayWithoutValues = <T,>(array: T[], values: T[]): T[] => _.difference(array, values);

// These handle comparing moments deep in object properties correctly.
export const arrayWithoutValuePolymorphic = <T,>(array: T[], value: T): T[] =>
    array.filter((element) => !polymorphicIsEqual(element, value));
export const arrayWithValuePolymorphic = <T,>(array: T[], value: T): T[] => [
    ...arrayWithoutValues(array, [value]),
    value,
];

export const objectWithValue = <T,>(
    obj: Record<string, T>,
    key: string,
    value: T,
): Record<string, T> => ({
    ...obj,
    [key]: value,
});

export const average = (array: number[]): number => array.reduce((a, b) => a + b) / array.length;

export const includeInArrayIf = <T,>(condition: boolean, item: T): T[] => (condition ? [item] : []);
export const includeAllInArrayIf = <T,>(condition: boolean, items: T[]): T[] =>
    condition ? items : [];

export const includeAllInObjectIf = <T extends object>(condition: boolean, items: T): T =>
    condition ? items : ({} as T);

export const only = <T,>(array: T[]): T => {
    if (array.length !== 1) {
        throw new Error('Expected array to have exactly one element');
    }
    return array[0];
};
export const getFrom = <T, K extends keyof T, V = T[K]>(options: T[], key: K, value: V): T => {
    return only(_.filter(options, [key, value]));
};

export const assertNotUndefined = <T,>(value: T | undefined): T => {
    if (value === undefined) {
        throw new Error('Value is undefined');
    }
    return value;
};
export const assertNotNull = <T,>(value: T | null): T => {
    if (value === null) {
        throw new Error('Value is undefined or null');
    }
    return value;
};

export const safeMax = (array: number[], fallback: number): number =>
    array.length === 0 ? fallback : Math.max(...array);

export const includesObject = <T,>(array: T[], obj: T): boolean =>
    array.some((element) => polymorphicIsEqual(element, obj));

export const findOrError = <T,>(array: T[], predicate: (element: T) => boolean): T => {
    const result = array.find(predicate);
    if (result === undefined) {
        throw new Error('Element not found');
    }
    return result;
};
